import React, { useEffect, useRef } from "react";
import { useRouter } from "next/router";
import dynamic from "next/dynamic";

const FullScreenLoader = dynamic(
  () => import("@/components/Molecules/FullScreenLoader")
);

import Headroom from "@/components/Molecules/Headroom";
import ShyftPassHeader from "@/components/Organisms/ShyftPassHeader";
import ShyftPassFooter from "@/components/Molecules/ShyftPassFooter";

import NotFound from "@/containers/NotFoundPage";
import ErrorBoundary from "@/containers/ErrorPage";

import theme from "@/foundation/Theme";

import { useAppSelector, useAppDispatch, useWindowDimensions } from "@/hooks";
import * as dashboardActions from "@/redux/DashboardReducer";
interface ShyftPassTemplateProps {
  children: React.ReactNode;
  // footerRef: propTypeRef,
  isLoading?: boolean;
  success?: boolean;
  hideHeader?: boolean;
  isStickyHeader?: boolean;
  hideFooter?: boolean;
  hideLinks?: boolean;
  isDarkMode?: boolean;
  error?: string;
  isFixedLoader?: boolean;
  headerBackgroundColor?: string;
  darkHeader?: boolean;
  hideRibbon?: boolean;
  appDownloadLink?: string;
  isShyftPassBookingPage?: boolean;
}

const ShyftPassTemplate: React.FC<ShyftPassTemplateProps> = ({
  isLoading,
  success = true,
  children,
  hideHeader,
  hideFooter,
  isStickyHeader = true,
  hideLinks,
  isDarkMode = true,
  error,
  // footerRef,
  isFixedLoader,
  darkHeader,
  headerBackgroundColor = theme.colorsV2.gray.shade_100,
  hideRibbon,
  appDownloadLink,
  isLandingPage,
  isShyftPassBookingPage = false,
  baseUrl,
}) => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const { width } = useWindowDimensions();
  const headerRef = useRef(null);

  const { error: APIError } = useAppSelector((state) => state.auth);
  const { ribbonDataSuccess, ribbonData, routeChangeLoading } = useAppSelector(
    (state) => state.dashboard
  );

  useEffect(() => {
    function handleResize() {
      if (ribbonDataSuccess) {
        const value = headerRef.current?.offsetHeight;
        // TODO:add debouncing
        dispatch(dashboardActions.setHeaderHeight(value));
      }
    }

    if (typeof window !== "undefined") {
      window.addEventListener("resize", handleResize);
      window.addEventListener("orientationchange", handleResize);
    }
    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("resize", handleResize);
        window.removeEventListener("orientationchange", handleResize);
      }
    };
  }, [ribbonDataSuccess]);

  if ((isLoading || routeChangeLoading) && !isFixedLoader) {
    return <FullScreenLoader />;
  }
  if (typeof APIError === "object" && Object.keys(APIError)?.length > 0) {
    if (APIError?.response?.status >= 500) {
      //NOTE: throwing this error to show error page
      throw new Error(APIError?.message);
    }
  }
  if (typeof error === "object" ? Object.keys(error)?.length > 0 : error) {
    return <NotFound />;
  }

  return (
    <ErrorBoundary
      darkHeader={darkHeader}
      hideLinks={hideLinks}
      windowWidth={width}
      headerBackgroundColor={headerBackgroundColor}
      hideRibbon={true}
      hideHeader={hideHeader}
      history={router}
    >
      {(isLoading || routeChangeLoading) && (
        <FullScreenLoader isFixedLoader={isFixedLoader} />
      )}
      {!hideHeader && (
        <Headroom disable={!isStickyHeader}>
          <ShyftPassHeader
            darkHeader={false}
            hideLinks={false}
            windowWidth={width}
            headerBackgroundColor={headerBackgroundColor}
            isStickyHeader={isStickyHeader}
            hideRibbon={true}
            appDownloadLink={appDownloadLink}
            headerRef={headerRef}
            isLandingPage={isLandingPage}
            isShyftPassBookingPage={isShyftPassBookingPage}
          />
        </Headroom>
      )}
      {children}
      {!hideFooter && <ShyftPassFooter baseUrl={baseUrl} />}
    </ErrorBoundary>
  );
};

export default ShyftPassTemplate;
